<template>
  <section>
    <div
      class="mb-8"
    >
      <h2
        class="leading-tight"
        :class="{
          'mb-4': type !== 'lead'
        }"
      >
        {{ company.name }}
      </h2>
      <p
        v-if="type === 'lead'"
        class="text-sm text-grey-700 mb-4"
      >
        Lead Company
      </p>
    </div>
    <div>
      <ul class="list-reset team-grid">
        <li class="card is-borderless p-6">
          <h3>
            Company Details
          </h3>
          <dl class="details">
            <dt>Company number:</dt>
            <dd>{{ company.number }}</dd>
            <dt>Company status:</dt>
            <dd>{{ company.companies_house_info.company_status || "TBC" }}</dd>
            <dt>Incorporation date:</dt>
            <dd>{{ company.companies_house_info.date_of_creation ? toShortDate(company.companies_house_info.date_of_creation) : "TBC" }}</dd>
            <dt>Registered address:</dt>
            <dd>{{ companyAddress }}</dd>
          </dl>
        </li>
        <li
          v-for="member in teamMembers"
          :key="member.user_id"
          class="flex"
        >
          <team-card
            class="w-full"
            :partner-id="partnerId"
            :member="member"
            :can-modify-team="canModifyTeam"
            @team:update="teamMemberUpdate"
            @team:delete="teamMemberDelete"
          />
        </li>
        <li
          v-if="canModifyTeam"
          class="flex"
        >
          <button
            class="bg-primary-100 uppercase text-primary-900 font-bold flex items-center transition-all transition-normal hover:bg-primary-200 w-full h-full justify-center border-2 border-dashed border-primary-300 rounded"
            @click="inviteTeamShow"
          >
            <div>
              <icon
                icon="user-circle"
                class="icons-primary w-12 h-12 block mx-auto m-6"
              />
              Add Team Member
            </div>
          </button>
        </li>
      </ul>
      <portal to="modals">
        <team-section-invite-modal
          v-if="inviteModal"
          ref="inviteTeamModal"
          :company="company"
          :invite-modal-notice="inviteModalNotice"
          @team:invite="inviteMember"
          @close="inviteTeamHide"
        />
      </portal>
      <div
        v-if="staff.length"
        class="card is-base my-12"
      >
        <h2 class="my-2 mx-4">
          Staff Rates
          <small class="block text-xs text-grey-700 font-normal">If you would like to add or amend your staff rates, please submit a change request.</small>
        </h2>
        <table class="w-full rates-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Job Title</th>
              <th>Gross Employee Cost (Per Year)</th>
              <th>Working Days Per Year</th>
              <th>Day Rate</th>
              <th>Hourly Rate</th>
              <th>Version</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="member in staff"
              :key="member.id"
            >
              <td>{{ member.name }}</td>
              <td>{{ member.job_title || "--" }}</td>
              <td>{{ toLocalCurrency(member.active_rate.gross_employee_cost) }} per year</td>
              <td>{{ member.active_rate.working_days_per_year }}</td>
              <td>{{ toLocalCurrency(member.active_rate.day_rate) }}</td>
              <td>{{ toLocalCurrency(member.active_rate.hourly_rate) }}</td>
              <td>v{{ member.active_rate.version }} <span class="text-grey-400">[{{ toShortDate(member.updated_at) }}]</span></td>
            </tr>
          </tbody>
          <tfoot v-if="staff.length > 5">
            <tr>
              <th>Name</th>
              <th>Job Title</th>
              <th>Version</th>
              <th>Gross Employee Cost (Per Year)</th>
              <th>Working Days Per Year</th>
              <th>Day Rate</th>
              <th>Hourly Rate</th>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </section>
</template>

<script>
/**
 * @typedef {object} Staff
 * @property {number} id
 * @property {string} name
 * @property {string} job_title
 * @property {string} rate_version
 * @property {string} updated_at
 * @property {ActiveRate} active_rate
 *
 * @typedef {object} ActiveRate
 * @property {number} version
 * @property {number} gross_employee_cost
 * @property {number} working_days_per_year
 * @property {number} day_rate
 * @property {number} hourly_rate
 */
import pick from "lodash/pick";
export default {
  name: "TeamSection",
  props: {
    type: {
      type: String,
      required: true,
      validator(type) {
        return ["lead", "partner"].includes(type);
      }
    },
    partnerId: {
      type: Number,
      required: true
    },
    company: {
      type: Object,
      required: true
    },
    team: {
      type: Array,
      required: true
    },
    /** @type import('vue').PropType<Array<Staff>> */
    staff: {
      type: Array,
      default: () => []
    },
    isAdmin: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      inviteModal: false,
      inviteModalNotice: {},
      teamMembers: []
    };
  },
  computed: {
    authUser() {
      return window._user || {};
    },
    canModifyTeam() {
      if (this.isAdmin) return true;
      if (this.authUser.isMonitor) return false;
      return this.authUser.isLead
        ? this.authUser.isEditor
        : this.authUser.company_id === this.company.id
        ? this.authUser.isPartner && this.authUser.isEditor
        : false;
    },
    authUserIsMember() {
      if (this.isAdmin) return false;
      return this.authUser.id === this.user.id;
    },
    companyAddress() {
      let address = pick(this.company, [
        "address_line_1",
        "address_line_2",
        "town",
        "county",
        "postcode"
      ]);
      return Object.values(address)
        .filter(Boolean)
        .join(", ");
    }
  },
  created() {
    this.teamMembers = this.team;
  },
  methods: {
    toLocalCurrency(amount) {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP"
      }).format(amount);
    },
    toShortDate(date) {
      return new Intl.DateTimeFormat("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(new Date(date));
    },
    getMemberName(id) {
      return (
        this.teamMembers.find(member => member.id === id).user.name || false
      );
    },

    inviteTeamShow() {
      return (this.inviteModal = true);
    },

    inviteTeamHide() {
      this.inviteModalNotice = {};
      return (this.inviteModal = false);
    },

    inviteMember(invite) {
      this.inviteRequest(invite)
        .then(data => this.successReponse(data))
        .catch(error => this.failedReponse(error));
    },

    inviteRequest(payload) {
      return axios.post(
        route("projects.team.invite", {
          project: window._project,
          partner: this.partnerId
        }),
        payload
      );
    },

    successReponse({ data }) {
      data.data.forEach(item => this.teamMembers.push(item));
      this.inviteModalNotice = {
        type: "positive",
        content: `<strong>Success:</strong> You have successfully added a member to this team.`
      };
      this.$refs.inviteTeamModal.inviteReset();
    },

    failedReponse(error) {
      const { data } = error.response;
      this.inviteModalNotice = {
        type: "negative",
        content: `<strong>Error:</strong> ${data?.message ?? 'Something went wrong, please try again.'}`,
        errors: data.errors
      };
      return error;
    },

    updateRequest(payload) {
      return axios.patch(
        route("projects.team.update", {
          project: window._project,
          partner: this.partnerId,
          user: payload.id
        }),
        payload
      );
    },

    deleteRequest(payload) {
      return axios.delete(
        route("projects.team.delete", {
          project: window._project,
          partner: this.partnerId,
          user: payload.id
        }),
        payload
      );
    },

    teamMemberUpdate(payload) {
      if (
        payload.action === "primary" &&
        !confirm(
          `Are you sure you want to make ${this.getMemberName(
            payload.id
          )} the primary contact for this team? You will be removed as the primary contact for this project.`
        )
      )
        return;
      this.updateRequest(payload)
        .then(({ data }) => {
          if (data.action === "primary") window.location.reload();
          let updatedMember = this.teamMembers.find(
            member => member.id === data.data.id
          );
          updatedMember.role = data.data.role;
        })
        .catch(error => alert(error.message));
    },

    teamMemberDelete(payload) {
      if (
        !confirm(
          `Are you sure you want to remove ${this.getMemberName(
            payload.id
          )} from your project?`
        )
      )
        return;
      this.deleteRequest(payload)
        .then(() => {
          this.teamMembers = [...this.teamMembers].filter(
            teamMember => teamMember.id !== payload.id
          );
        })
        .catch(error => alert(error.message));
    }
  }
};
</script>
<style scoped>

.team-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(18rem, 1fr));
  grid-auto-rows: 1fr;
  grid-gap: 1.5rem;
}

.rates-table {
  border-collapse: collapse;
}

.rates-table thead,
.rates-table tfoot {
  @apply bg-primary-100;
}

.rates-table th {
  @apply text-xs uppercase tracking-wide;
}

.rates-table th,
.rates-table td {
  @apply text-left py-2 px-4 border;
}

.rates-table tbody tr:nth-child(even) {
  @apply bg-grey-100;
}

.details {
  @apply text-sm;
  display: grid;
  grid-gap: 0.25rem 1.5rem;
}

.details dt {
  @apply text-grey-700;
}

</style>
